import { DOCUMENT } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, inject, Input } from '@angular/core';
import type { Image } from '@core-mkt/interfaces/image';
import { EnvService } from '@core-mkt/services/env/env.service';
import { canonicalIdBase } from '@core-mkt/services/schema/schema-id.util';
import { getBrandSiteUrl } from '@core-mkt/services/schema/organization.schema';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import type { ResourceCard, Resources } from '@core-mkt/shared/components/resources/resources/resources';
import { ResourcesComponent } from '@core-mkt/shared/components/resources/resources/resources.component';
import type { FeaturedEntry } from './featured-article';
import { FeaturedArticle } from './featured-article';
import { getFeaturedArticleSchema } from './featured-article.schema';

@Component({
  selector: 'featured-article',
  templateUrl: './featured-article.component.html',
  standalone: true,
  imports: [ResourcesComponent],
})
export class FeaturedArticleComponent implements OnInit {
  @Input() data: FeaturedArticle;
  resourcesData: Resources = null;

  private envService = inject(EnvService);
  private schemaService = inject(SchemaService);
  private document = inject<Document>(DOCUMENT);

  ngOnInit(): void {
    if (this.data) {
      this.resourcesData = this.transformToResourcesData(this.data);
      this.registerSchema();
    }
  }

  private registerSchema(): void {
    const organizationName = this.envService.get?.brandConfig?.name ?? '';
    const brandUrl = this.envService.get?.brandConfig?.url;
    // Skip when brand config can't yield a valid Organization — avoids emitting an
    // invalid/empty siteUrl and broken #organization cross-references.
    if (!organizationName || !brandUrl) return;
    // Canonical (origin + pathname, no query/hash) so the article/ItemList @ids stay
    // stable across UTM/coupon variants of the same page.
    const pageUrl = canonicalIdBase(this.document.URL);
    // Canonical brand site root for the org cross-ref and article URLs — never the
    // runtime document.URL, which would fragment the org @id per environment. EPS-3951.
    const siteUrl = getBrandSiteUrl(brandUrl);
    this.schemaService.register(getFeaturedArticleSchema({ pageUrl, siteUrl, organizationName, data: this.data }));
  }

  private transformToResourcesData(data: FeaturedArticle): Resources {
    const featuredEntries = data.featuredArticles || [];

    const resourceCards: ResourceCard[] = featuredEntries.map((entry) => {
      const textContent = `<h4>${entry.title}</h4>${entry.blogSummary ? `<p>${entry.blogSummary}</p>` : ''}`;

      const resourceImage = this.getResourceImage(entry);

      return {
        resourceImage,
        resourceBackgroundColor: ['bg-vis-white', 'text-vis-regular'],
        resourceTextContent: textContent,
        resourceCtaStyle: 'tertiary',
        resourceCtaLabel: 'Read More',
        resourceCtaLink: `/${this.slugToPath(entry.slug)}`,
        resourceCtaLinkNewTab: false,
        resourceCtaLinkNofollow: false,
        resourceCtaTag: undefined,
      };
    });

    // Apply CTA link overrides if provided
    if (data.cardCtaOverride && data.cardCtaOverride.length > 0) {
      data.cardCtaOverride.forEach((override) => {
        const cardIndex = parseInt(override.cardNumber, 10) - 1; // cardNumber is 1-based
        if (cardIndex >= 0 && cardIndex < resourceCards.length && override.ctaCardOverrideLink) {
          resourceCards[cardIndex].resourceCtaLink = override.ctaCardOverrideLink;
        }
      });
    }

    return {
      styles: data.styles && data.styles.length > 0 ? data.styles : ['bg-white', 'text-vis-regular'],
      resourcesBackgroundImage: [],
      resourcesTitle: data.componentTitle,
      resourcesTextColor: '',
      resourceCards,
    };
  }

  private getResourceImage(entry: FeaturedEntry): Image[] {
    if (entry.featuredEntryImage && entry.featuredEntryImage.length > 0) {
      return entry.featuredEntryImage;
    }

    return [
      {
        url: this.envService.get.defaultOgImage,
        altText: entry.title,
      },
    ];
  }

  private slugToPath(slug: string): string {
    return slug.replace(/--/g, '/');
  }
}
